/**
 * PromptDialog (DESIGN.md T4 in a dialog): one named text value, e.g. a folder name for "New
 * folder" or "Rename". Required, trimmed; the dialog stays open and busy while `onSubmit` runs
 * and keeps the input when it fails (the caller shows the error toast).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/form';
import { Input } from '../components/input';
import { useUiStrings } from '../strings';

export interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  label: string;
  /** Validation message for an empty value. */
  requiredMessage: string;
  /** The verb, e.g. "Create" or "Rename". */
  confirmLabel: string;
  initialValue?: string;
  /** Resolves when done (the dialog closes); rejects to keep it open. */
  onSubmit: (value: string) => Promise<void> | void;
  /** Extra fields below the input (e.g. a visibility choice). */
  children?: ReactNode;
  /** Inline error under the input (e.g. a name that already exists). */
  error?: string;
}

export function PromptDialog({ open, onOpenChange, title, label, requiredMessage, confirmLabel, initialValue = '', onSubmit, children, error }: PromptDialogProps) {
  const strings = useUiStrings();
  const schema = useMemo(() => z.object({ value: z.string().trim().min(1, requiredMessage) }), [requiredMessage]);
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { value: initialValue } });

  const submit = form.handleSubmit(async ({ value }) => {
    try {
      await onSubmit(value);
      onOpenChange(false);
    } catch {
      // The caller reported the error; the input stays for a retry.
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4" noValidate>
            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                  {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
                </FormItem>
              )}
            />
            {children}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {strings.cancel}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
